// src/services/spx6900Price.ts

/**
 * Centralized SPX6900 Price Service
 * Handles rate limiting, caching, and deduplication of price requests
 */

import axios from 'axios';
import { getCachedData, getStaleData, setCachedData } from './cache';

// Rate limiting for SPX6900 price fetching
let spx6900PriceFetchPromise: Promise<number> | null = null;
let lastSpx6900PriceFetch = 0;
const SPX6900_PRICE_RATE_LIMIT = 5 * 60 * 1000; // 5 minutes minimum between API calls
const SPX6900_PRICE_CACHE_TTL = 2 * 60 * 60; // 2 hours cache TTL

/**
 * Get SPX6900 price with intelligent caching and rate limiting
 * This is the single source of truth for SPX6900 price throughout the app
 */
export const getSPX6900Price = async (): Promise<number> => {
  try {
    // First check cache for fresh data
    const cachedPrice = await getCachedData<number>('spx6900_price');
    if (cachedPrice && cachedPrice > 0.01) {
      console.log(`📦 Using cached SPX6900 price: $${cachedPrice}`);
      return cachedPrice;
    }

    // Check if we have an ongoing fetch to avoid duplicate API calls
    if (spx6900PriceFetchPromise) {
      console.log('🔄 Waiting for ongoing SPX6900 price fetch...');
      return await spx6900PriceFetchPromise;
    }

    // Check rate limiting
    const now = Date.now();
    if (now - lastSpx6900PriceFetch < SPX6900_PRICE_RATE_LIMIT) {
      console.log('⏳ Rate limited - using stale data for SPX6900 price');
      const stalePrice = await getStaleData<number>('spx6900_price');
      if (stalePrice && stalePrice > 0.01) {
        return stalePrice;
      }
    }

    // Create a single fetch promise to prevent duplicate calls
    spx6900PriceFetchPromise = fetchSPX6900PriceFromAPI();
    const price = await spx6900PriceFetchPromise;
    spx6900PriceFetchPromise = null; // Reset promise after completion
    
    return price;
  } catch (error) {
    console.error('❌ Error getting SPX6900 price:', error);
    
    // Try to get any stale data as fallback
    const stalePrice = await getStaleData<number>('spx6900_price');
    if (stalePrice && stalePrice > 0.01) {
      console.log(`🔄 Using stale SPX6900 price as fallback: $${stalePrice}`);
      return stalePrice;
    }
    
    // Last resort fallback (not cached)
    console.warn('⚠️ Using hardcoded fallback SPX6900 price: $0.001 (NOT CACHED)');
    return 0.001;
  }
};

/**
 * Fetch SPX6900 price from CoinGecko API
 */
const fetchSPX6900PriceFromAPI = async (): Promise<number> => {
  try {
    lastSpx6900PriceFetch = Date.now();
    
    console.log('🌐 Fetching SPX6900 price from CoinGecko API...');
    const response = await axios.get('https://api.coingecko.com/api/v3/simple/price', {
      params: {
        ids: 'spx6900',
        vs_currencies: 'usd',
        include_24hr_change: true
      },
      timeout: 10000
    });
    
    const fetchedPrice = response.data.spx6900?.usd;
    
    // Validate the price before caching
    if (fetchedPrice && fetchedPrice > 0.01 && fetchedPrice < 1000) {
      await setCachedData('spx6900_price', fetchedPrice, SPX6900_PRICE_CACHE_TTL);
      console.log(`✅ SPX6900 price fetched and cached: $${fetchedPrice} (TTL: ${SPX6900_PRICE_CACHE_TTL / 3600}h)`);
      return fetchedPrice;
    } else {
      console.warn(`⚠️ Invalid SPX6900 price from API: ${fetchedPrice}`);
      
      // Try to get stale data instead of using fallback
      const stalePrice = await getStaleData<number>('spx6900_price');
      if (stalePrice && stalePrice > 0.01) {
        console.log(`🔄 Using stale SPX6900 price due to invalid API response: $${stalePrice}`);
        return stalePrice;
      }
      
      return 0.001; // Fallback for truly invalid data
    }
  } catch (error) {
    console.error('❌ SPX6900 price fetch from API failed:', error);
    
    // Try to get stale data on API failure
    const stalePrice = await getStaleData<number>('spx6900_price');
    if (stalePrice && stalePrice > 0.01) {
      console.log(`🔄 Using stale SPX6900 price due to API failure: $${stalePrice}`);
      return stalePrice;
    }
    
    return 0.001; // Final fallback
  }
};

/**
 * Get a reasonable SPX6900 price estimate for calculations
 * This will prefer cached/stale data over making new API calls
 */
export const getSPX6900PriceForCalculations = async (): Promise<number> => {
  try {
    // For calculations, prefer any cached data (even stale) over API calls
    const cachedPrice = await getCachedData<number>('spx6900_price');
    if (cachedPrice && cachedPrice > 0.01) {
      return cachedPrice;
    }
    
    const stalePrice = await getStaleData<number>('spx6900_price');
    if (stalePrice && stalePrice > 0.01) {
      console.log(`🔄 Using stale SPX6900 price for calculations: $${stalePrice}`);
      return stalePrice;
    }
    
    // Only make API call if absolutely no data exists
    return await getSPX6900Price();
  } catch (error) {
    console.warn('⚠️ Error getting SPX6900 price for calculations, using fallback');
    return 0.001;
  }
};